import React from 'react';

export interface IconComponentProps {
  icon: React.ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  'data-testid'?: string;
}

export interface KpiTileProps {
  title: string;
  description?: string;
  iconComponent?: IconComponentProps;
  /** When provided, the whole tile becomes a toggle button. */
  onClick?: () => void;
  /** Toggled/active state — drives the "selected" accent and `aria-pressed`. */
  selected?: boolean;
  /** Accessible name for the toggle button (falls back to the title). */
  ariaLabel?: string;
}

type IconSize = NonNullable<IconComponentProps['size']>;

// Per-size sizing for the floating badge. All Tailwind classes are literal
// strings so the JIT can include them at build time.
const SIZE_CONFIG: Record<IconSize, {
  badgeClass: string;
  badgeOffsetClass: string;
  iconClass: string;
  rightPaddingClass: string;
}> = {
  xs: { badgeClass: 'h-10 w-10', badgeOffsetClass: '-top-2 -right-2',     iconClass: 'h-5 w-5',   rightPaddingClass: 'pr-14' },
  sm: { badgeClass: 'h-12 w-12', badgeOffsetClass: '-top-2.5 -right-2.5', iconClass: 'h-6 w-6',   rightPaddingClass: 'pr-16' },
  md: { badgeClass: 'h-16 w-16', badgeOffsetClass: '-top-3 -right-3',     iconClass: 'h-7 w-7',   rightPaddingClass: 'pr-20' },
  lg: { badgeClass: 'h-20 w-20', badgeOffsetClass: '-top-4 -right-4',     iconClass: 'h-9 w-9',   rightPaddingClass: 'pr-24' },
  xl: { badgeClass: 'h-24 w-24', badgeOffsetClass: '-top-5 -right-5',     iconClass: 'h-11 w-11', rightPaddingClass: 'pr-28' },
};

// Card + badge share the same soft elevation defined as `--tile-shadow` in
// index.css (light/dark switched). No clip-path here, so `border` paints
// cleanly around the whole rectangle and `box-shadow` from the filter chain
// reads as a normal drop.
const TILE_FILTER_STYLE: React.CSSProperties = {
  filter: 'var(--tile-shadow)',
};

// Append the size class to whatever className the icon already has, so colour
// utilities the caller passed (e.g. `text-primary-500`) survive.
const withIconSize = (icon: React.ReactNode, iconClass: string): React.ReactNode => {
  if (!React.isValidElement(icon)) return icon;
  const existing = (icon.props as { className?: string }).className;
  return React.cloneElement(
    icon as React.ReactElement<{ className?: string }>,
    { className: existing ? `${existing} ${iconClass}` : iconClass }
  );
};

export const KpiTile: React.FC<KpiTileProps> = ({
  title,
  description,
  iconComponent,
  onClick,
  selected = false,
  ariaLabel,
}) => {
  const icon = iconComponent?.icon;
  const config = SIZE_CONFIG[iconComponent?.size ?? 'md'];
  const sizedIcon = icon ? withIconSize(icon, config.iconClass) : null;
  const interactive = typeof onClick === 'function';

  // Minimal-accent selected cue: a primary border on the card and a
  // primary-tinted icon badge. The card surface and the icon art are left
  // untouched so multi-coloured glyphs keep their own colours.
  const cardBorder = selected
    ? 'border-primary-500'
    : `border-border-primary/40${interactive ? ' group-hover:border-primary-300' : ''}`;
  const badgeBorder = selected ? 'border-primary-500' : 'border-border-primary/40';
  const badgeBg = selected ? 'bg-primary-50 dark:bg-primary-950/40' : 'bg-white dark:bg-grey-900';

  const content = (
    <>
      <div
        className={`bg-white dark:bg-grey-900 border ${cardBorder} rounded-2xl px-6 py-5 transition-colors duration-fast ease-out-strong ${icon ? config.rightPaddingClass : ''}`}
        style={TILE_FILTER_STYLE}
      >
        {/* Text stack — line-heights match Figma tokens (h3/line-height=48,
            subtitle2/line-height=22). The generous title leading creates the
            visual gap between the rows on its own. In the interactive (button)
            variant the title/description are spans — flow elements like <h2>/<p>
            are invalid inside a <button>. */}
        <div className="flex flex-col">
          {interactive ? (
            <span className="text-xl font-bold text-text-primary leading-[48px]">{title}</span>
          ) : (
            <h2 className="text-xl font-bold text-text-primary leading-[48px]">{title}</h2>
          )}
          {description &&
            (interactive ? (
              <span className="text-sm font-semibold text-text-secondary leading-[22px]">
                {description}
              </span>
            ) : (
              <p className="text-sm font-semibold text-text-secondary leading-[22px]">
                {description}
              </p>
            ))}
        </div>
      </div>

      {sizedIcon && (
        <div
          className={`absolute ${config.badgeOffsetClass} flex ${config.badgeClass} items-center justify-center rounded-full border ${badgeBorder} ${badgeBg} transition-colors duration-fast ease-out-strong`}
          style={TILE_FILTER_STYLE}
        >
          {sizedIcon}
        </div>
      )}
    </>
  );

  if (interactive) {
    return (
      <button
        type="button"
        onClick={onClick}
        aria-pressed={selected}
        aria-label={ariaLabel ?? title}
        className="group relative mt-4 mr-4 text-left rounded-2xl transition-transform duration-fast ease-out-strong active:scale-[0.98] focus-ring"
      >
        {content}
      </button>
    );
  }

  return <div className="relative mt-4 mr-4">{content}</div>;
};
